import { useEffect, useState } from 'react';

import { Banner, Button, Col, Row } from 'darrius';

import { trackOneSignalEvent } from '../../../utils/onesignal';

import Image from 'next/image';

import { Countdown, Loading, Otp } from '../../../components';

import { imgPrefix, navigate } from '../../../utils';

import { useAuth } from '../../../hooks/auth/useAuth';

import styles from './SignInPin.module.scss';

import { type TOtpPinStatus } from '../../../components/atoms/Otp/Otp';
import { type TPinScreen } from '../../../pages/entrar';
import { useClientUser } from '../../../hooks/useClientUser';

interface ISignInPinProps {
  path?: string;
  i18n: TPinScreen;
  urls: {
    validate: string;
    resend: string;
  };
  userPin?: string;
  isAuthOrder?: boolean;
  uuid?: string;
}

const defaultI18n: TPinScreen = {
  title: 'Verificação de PIN',
  subtitle: 'Digite o código PIN que enviamos para o número',
  resendSms: {
    button: { text: 'Reenviar código' },
    countdown: {
      timeInSeconds: 60,
      text: { prefix: 'Aguarde', suffix: 'segundos para reenviar' },
    },
  },
};

const IS_PIN_BYPASS_ACTIVE = true;
const BYPASS_VALIDATE_TIME = 2000;

export function SignInPin({
  path = '/',
  i18n,
  urls,
  isAuthOrder = false,
  userPin,
  uuid,
}: ISignInPinProps) {
  const [pin, setPin] = useState('');
  const [isPinSending, setIsPinSending] = useState(false);
  const [pinStatus, setPinStatus] = useState<TOtpPinStatus>('default');
  const [previouslyEnteredPin, setPreviouslyEnteredPin] = useState('');

  const { subtitle, resendSms, title } = i18n || defaultI18n;

  const { handleUserSignOut } = useClientUser({
    shouldFetchUserData: false,
  });

  const handleSendSuccessLogNewrelic = () => {
    if (window?.newrelic) {
      window.newrelic.addPageAction('create_order_action', {
        step: 'submited_pin_success',
      });
    }
  };

  const handleSendErrorLogNewrelic = (errorInfo: any) => {
    if (window?.newrelic) {
      window.newrelic.noticeError(errorInfo, {
        step: 'submited_pin_error',
        payload: errorInfo?.config?.data,
        status: errorInfo?.status,
        errorMessage: errorInfo?.message,
      });

      window.newrelic.addPageAction('create_order_action', {
        step: 'submited_pin_error',
        payload: errorInfo?.config?.data,
        status: errorInfo?.status,
        errorMessage: errorInfo?.message,
      });
    }

    trackOneSignalEvent('error_aconteceu', {
      nome_do_erro: errorInfo?.message,
    });
  };

  const { onSubmitPin, onResendPin, countDown } = useAuth(
    urls?.validate,
    urls?.validate
  );

  const {
    onResetCountdown,
    onCountdownOver,
    isShouldResetCountdown,
    isCountdownOver,
  } = countDown;

  useEffect(() => {
    const handlePin = async () => {
      if (pin.length === 4 && pin !== previouslyEnteredPin) {
        setPreviouslyEnteredPin(pin);
        setIsPinSending(true);

        const { success, error } = await onSubmitPin({
          pin,
        });

        if (success) {
          setPinStatus('success');
          setIsPinSending(false);

          const timer = setTimeout(() => {
            const link = `${process.env.NEXT_PUBLIC_HOST}${path}`;

            if (isAuthOrder) {
              handleSendSuccessLogNewrelic();
            }

            navigate(link);
          }, 2000);

          return () => {
            clearTimeout(timer);
          };
        }

        if (isAuthOrder && error) {
          handleSendErrorLogNewrelic(error);
        }
        setPinStatus('error');
        setIsPinSending(false);
      }
    };

    handlePin();
  }, [onSubmitPin, path, pin, previouslyEnteredPin]);

  useEffect(() => {
    console.log('ANTES DO BYPASS');
    if (IS_PIN_BYPASS_ACTIVE && isAuthOrder && userPin) {
      console.log('DENTRO DO BYPASS');
      const handlePin = async () => {
        setIsPinSending(true);

        const { success, error } = await onSubmitPin({
          pin: userPin,
        });

        if (success) {
          setPinStatus('success');
          setIsPinSending(false);

          handleSendSuccessLogNewrelic();

          const timer = setTimeout(async () => {
            await handleUserSignOut(isAuthOrder, uuid);
          }, BYPASS_VALIDATE_TIME);

          return () => {
            clearTimeout(timer);
          };
        }

        if (error) {
          handleSendErrorLogNewrelic(error);
        }
        setIsPinSending(false);
      };
      handlePin();
    }
  }, []);

  async function handleResendPin() {
    await onResendPin();
  }

  function handleChangePin(pinValue: string) {
    setPinStatus('default');
    setPin(pinValue);
  }

  return (
    <Banner>
      <Row>
        <Col className={styles.auth__content}>
          <Image
            src={imgPrefix('/images/auth/sms-code.svg')}
            alt=""
            height={96}
            width={96}
          />

          <Banner>
            <h1>{title}</h1>
            <p>{subtitle}</p>

            <Otp
              handleChangePin={handleChangePin}
              isPinSending={isPinSending}
              pinStatus={pinStatus}
            />

            {isPinSending && (
              <div className={styles.pin_loading}>
                <Loading />
              </div>
            )}

            <Countdown
              onIsCountdownOver={onCountdownOver}
              timeToCountDown={resendSms.countdown.timeInSeconds}
              isShouldResetCountdown={{
                canReset: isShouldResetCountdown,
                onReset: onResetCountdown,
              }}
              textToShow={{
                prefix: resendSms.countdown.text.prefix,
                suffix: resendSms.countdown.text.suffix,
              }}
            />

            <Button.Root
              onClick={handleResendPin}
              className={styles.resend_button}
              variation="clear"
              disabled={!isCountdownOver || isPinSending}
              size="small"
            >
              {resendSms.button.text}
            </Button.Root>
          </Banner>
        </Col>
      </Row>
    </Banner>
  );
}
